import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { isPublished } from '@shared/competitionRules';

const client = generateClient({ authMode: 'userPool' });

interface HappySnapPhotoWallProps {
  eventId: string;
  onBack: () => void;
}

const HappySnapPhotoWall: React.FC<HappySnapPhotoWallProps> = ({ eventId, onBack }) => {
  const [photos, setPhotos] = useState<any[]>([]);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [notPublished, setNotPublished] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        // Check event state
        const evtResult: any = await client.models.Event.get({ id: eventId });
        if (!isPublished(evtResult.data)) { setNotPublished(true); setLoading(false); return; }

        // Fetch photos
        let all: any[] = [];
        let nextToken: string | null = null;
        do {
          const result: any = await (client.models as any).CompetitionPhoto.list({ filter: { eventId: { eq: eventId } }, ...(nextToken ? { nextToken } : {}) });
          all = all.concat(result.data || []);
          nextToken = result.nextToken ?? null;
        } while (nextToken);

        const visible = all.filter((p: any) => p.status === 'APPROVED' || p.status === 'WINNER');
        // Winner first
        visible.sort((a: any, b: any) => (b.status === 'WINNER' ? 1 : 0) - (a.status === 'WINNER' ? 1 : 0));
        setPhotos(visible);

        // Load URLs
        const urls: Record<string, string> = {};
        await Promise.allSettled(visible.map(async (p: any) => {
          try {
            const r = await (client.queries as any).generateDownloadUrl({ s3Key: p.photoS3Key });
            const d = parseLambdaResponse(r?.data);
            if (d?.downloadUrl) urls[p.id] = d.downloadUrl;
          } catch { /* skip */ }
        }));
        setPhotoUrls(urls);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [eventId]);

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">Loading photo wall…</div>;

  if (notPublished) return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto text-center">
        <button onClick={onBack} className="mb-4 text-sm text-fishing-blue hover:underline">← Back</button>
        <div className="text-5xl mb-3">🖼️</div>
        <p className="text-gray-600">Results not yet available. The photo wall will be visible once the competition is published.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">🖼️ Happy Snaps Photo Wall</h1>
          <button onClick={onBack} className="text-sm text-fishing-blue hover:underline">← Back to Competitions</button>
        </div>

        {photos.length === 0 ? (
          <div className="text-center py-12"><p className="text-gray-500">No photos to display yet.</p></div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((p: any) => (
              <div key={p.id} className={`bg-white rounded-lg shadow-sm overflow-hidden ${p.status === 'WINNER' ? 'ring-4 ring-yellow-400' : 'border border-gray-200'}`}>
                {photoUrls[p.id] ? (
                  <img src={photoUrls[p.id]} alt={p.caption || 'Happy snap'} className="w-full h-48 object-cover" />
                ) : (
                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center text-4xl">📷</div>
                )}
                <div className="p-3">
                  {p.status === 'WINNER' && <div className="text-sm font-bold text-yellow-600 mb-1">🏆 Winner</div>}
                  <div className="text-sm font-medium text-gray-900">{p.memberName}</div>
                  {p.caption && <div className="text-xs text-gray-500 mt-1">{p.caption}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HappySnapPhotoWall;
